import React, { useState, useEffect } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

export interface OrderSummaryBoxProps {
  logo: React.FC;
  planName: string;
  productPrice: string;
  discount: string;
  total: string;
  installment: string;
  features: string[];
  // Optional props for success page
  isSuccessPage?: boolean;
  status?: string;
  startDate?: string;
  endDate?: string;
}

const OrderSummaryBox: React.FC<OrderSummaryBoxProps> = ({
  logo: Logo,
  planName,
  productPrice,
  discount,
  total,
  installment,
  features,
  isSuccessPage = false,
  status,
  startDate,
  endDate
}) => {
  const [isFeatureListOpen, setIsFeatureListOpen] = useState(false);

  // Check if it's mobile screen and collapse by default
  useEffect(() => {
    const checkScreenSize = () => {
      setIsFeatureListOpen(window.innerWidth >= 768); // md breakpoint
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const toggleFeatureList = () => {
    setIsFeatureListOpen(!isFeatureListOpen);
  };
  return (

    <div className="bg-white rounded-lg shadow-md p-6 flex flex-col gap-4 md:min-h-[400px] md:max-w-md mx-auto">
      <div className="flex flex-col items-center mb-6 hidden md:block">
        <Logo />
      </div>

      {isSuccessPage ? (
        // Success page layout
        <>
          <div>
            <h1 className="font-bold mb-3 text-xl">Resumo</h1>
            
            <div className="flex justify-between items-center my-2">
              <span className="text-gray-700">Status</span>
              <span className="font-medium text-green-600">{status}</span>
            </div>
            
            <div className="flex justify-between items-center my-2">
              <span className="text-gray-700">Valor</span>
              <span className="font-medium">{total}</span>
            </div>
            
            <div className="flex justify-between items-center my-2">
              <span className="text-gray-700">Início da assinatura</span>
              <span className="font-medium">{startDate}</span>
            </div>
            
            <div className="flex justify-between items-center my-2">
              <span className="text-gray-700">Término da assinatura</span>
              <span className="font-bold">{endDate}</span>
            </div>
          </div>
          <hr className="my-2" />
        </>
      ) : (
        <>
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Plano de assinatura</span>
            <span className="font-medium">{planName}</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-gray-700">Valor do produto</span>
            <span className="font-medium line-through text-gray-500">
              {productPrice}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-gray-700">Desconto aplicado</span>
            <span className="font-medium text-red-600">{discount}</span>
          </div>

          <hr className="my-2" />

          <div className="flex justify-between items-center font-bold">
            <span>Total</span>
            <span>{total}</span>
          </div>

          <div className="flex justify-between items-center text-green-500">
            <span className="font-medium">Você pagará</span>
            <span className="font-bold">{installment}</span>
          </div>
        </>
      )}

      <div className="mt-4">
        {/* Desktop: Always show title, Mobile: Show collapsible button */}
        <h3 className="hidden md:block font-bold mb-3">O que tem em sua assinatura:</h3>
        
        <button
          onClick={toggleFeatureList}
          className="md:hidden flex items-center justify-between w-full font-bold mb-3 text-left focus:outline-none hover:text-gray-700 transition-colors"
        >
          <span>O que tem em sua assinatura:</span>
          {isFeatureListOpen ? (
            <ChevronUp size={20} className="text-gray-500" />
          ) : (
            <ChevronDown size={20} className="text-gray-500" />
          )}
        </button>
        
        {/* Desktop: Always visible, Mobile: Collapsible */}
        <div className={`md:block overflow-hidden transition-all duration-300 ease-in-out ${
          isFeatureListOpen ? 'opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <ul className="space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex">
                <Check className="text-green-500 mr-2 min-w-[20px]" size={20} />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OrderSummaryBox;
